import { CircleHelpIcon, MailPlusIcon, TrashIcon } from 'lucide-react'

import { Button } from '@/components/button/button'
import { Checkbox } from '@/components/checkbox/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/tabs/tabs'

import { UserRole } from '@/lib/queries/business/types'
import { type ResUserQuery } from '@/lib/queries/business/v2/user/types'
import { cn } from '@/lib/utils'

import { DeleteMemberDialog } from '../dialogs/delete-member-dialog/delete-member-dialog'
import { InviteMemberDialog } from '../dialogs/invite-member-dialog/invite-member-dialog'
import { MemberTypeGuideDialog } from '../dialogs/member-type-guide-dialog/member-type-guide-dialog'
import { TAB_ITEMS, TabItemValues } from './constants'
import { Pagination } from './pagination'
import { PermissionMenu } from './permission-menu'
import { ToolsMenu } from './ui/tools-menu/tools-menu'
import { useMembers } from './use-members'

export interface MembersProps {
  user: ResUserQuery
}

export const Members: React.FC<MembersProps> = ({ user }) => {
  const {
    currentTabValue,
    membersPaged,
    page,
    totalPages,
    checkedMembers,
    checkedMemberIds,
    isPendingUpdateUser,
    isInviteMemberDialogOpen,
    isMemberDeleteDialogOpen,
    isMemberTypeGuideDialogOpen,
    isAllChecked,
    showsPagination,
    setPage,
    openInviteMemberDialog,
    openMemberTypeGuideDialog,
    closeInviteMemberDialog,
    closeMemberDeleteDialog,
    closeMemberTypeGuideDialog,
    resetCheckedMemberIds,
    handleRoleChange,
    handleChangeTabValue,
    handleChangeAllCheck,
    handleChangeMemberChecked,
    handleClickMemberDeleteButton,
  } = useMembers({ user })

  return (
    <>
      <div className="space-y-2">
        <div className={cn('flex items-center', 'text-sm font-medium')}>
          Members
          <button type="button" className={cn('ml-1')} onClick={openMemberTypeGuideDialog}>
            <CircleHelpIcon size={20} fill="#ABAFC9" className={cn('text-white')} />
          </button>
        </div>

        <div className={cn('flex justify-between')}>
          <Tabs value={currentTabValue} onValueChange={handleChangeTabValue}>
            <TabsList>
              {TAB_ITEMS.map(({ label, value }) => (
                <TabsTrigger key={value} value={value}>
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {currentTabValue === TabItemValues.members && (
            <Button
              type="button"
              variant="black"
              size="sm"
              disabled={checkedMemberIds.size === 0}
              onClick={handleClickMemberDeleteButton}
            >
              <TrashIcon size={16} className={cn('mr-2')} />
              Delete
            </Button>
          )}
        </div>

        <div className="rounded-md border">
          {membersPaged.length === 0 && (
            <div
              className={cn(
                'flex items-center justify-center',
                'w-full',
                'h-full',
                'p-6',
                'text-sm text-muted-foreground'
              )}
            >
              No data.
            </div>
          )}
          {membersPaged.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className={cn('text-xs font-medium')}>
                  {currentTabValue === TabItemValues.members && (
                    <TableHead className={cn('flex items-center')}>
                      <Checkbox variant="black" checked={isAllChecked} onCheckedChange={handleChangeAllCheck} />
                    </TableHead>
                  )}
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Team Permissions</TableHead>
                  <TableHead>{/** intended empty head cell */}</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {membersPaged.map((memberData) => (
                  <TableRow key={memberData.email} className="text-xs font-medium">
                    {currentTabValue === TabItemValues.members && (
                      <TableCell className={cn('flex items-center')}>
                        <Checkbox
                          variant="black"
                          disabled={
                            memberData.role === UserRole.SUPER_ADMIN ||
                            memberData.role === UserRole.ADMIN ||
                            memberData.id === user?.user.id
                          }
                          checked={checkedMemberIds.has(memberData.id)}
                          onCheckedChange={() => handleChangeMemberChecked(memberData.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>{memberData.name}</TableCell>
                    <TableCell>{memberData.email}</TableCell>
                    <TableCell>
                      <PermissionMenu
                        disabled={isPendingUpdateUser}
                        readOnly={user?.user.id === memberData.id || memberData.role === UserRole.SUPER_ADMIN}
                        role={memberData.role}
                        onRoleChange={(role) => handleRoleChange(memberData.id, role)}
                      />
                    </TableCell>
                    <TableCell>
                      {currentTabValue === TabItemValues.invitesPending && <ToolsMenu memberData={memberData} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="flex justify-between">
          <Button className="flex justify-between" variant="outline" onClick={openInviteMemberDialog}>
            <MailPlusIcon className="mr-2" size={16} />
            Invite members via email
          </Button>

          {showsPagination && <Pagination totalPages={totalPages} currentPage={page} onPageChange={setPage} />}
        </div>
      </div>
      {/* User invite dialog */}
      {isInviteMemberDialogOpen && (
        <InviteMemberDialog open={isInviteMemberDialogOpen} onClose={closeInviteMemberDialog} />
      )}
      {/* User delete dialog */}
      {isMemberDeleteDialogOpen && (
        <DeleteMemberDialog
          open={isMemberDeleteDialogOpen}
          deletedMembers={checkedMembers}
          onClose={closeMemberDeleteDialog}
          onDeleteSuccess={resetCheckedMemberIds}
        />
      )}
      {/* Member type guide dialog */}
      {isMemberTypeGuideDialogOpen && (
        <MemberTypeGuideDialog open={isMemberTypeGuideDialogOpen} onClose={closeMemberTypeGuideDialog} />
      )}
    </>
  )
}
